<app-page-breadcrumb [pageTitle]="pageTitle" />

<div class="mx-auto max-w-7xl space-y-6">
  @if (error) {
    <div class="rounded-xl border border-error-200 bg-error-50 p-4 dark:border-error-500/20 dark:bg-error-500/10">
      <p class="text-sm text-error-600 dark:text-error-400">{{ error }}</p>
    </div>
  }

  @if (templateError) {
    <div class="rounded-xl border border-warning-200 bg-warning-50 p-4 dark:border-warning-500/20 dark:bg-warning-500/10">
      <p class="text-sm text-warning-700 dark:text-warning-400">{{ templateError }}</p>
    </div>
  }

  @if (loading) {
    <div class="rounded-2xl border border-gray-200 bg-white px-6 py-16 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div class="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-brand-500"></div>
      <p class="mt-3 text-sm text-gray-500 dark:text-gray-400">Cargando configuración del chatflow...</p>
    </div>
  } @else {
    <div class="grid gap-6 xl:grid-cols-[minmax(0,1.7fr)_minmax(360px,0.95fr)]">
      <div class="space-y-6">
        <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div class="mb-6">
            <h2 class="text-xl font-semibold text-gray-800 dark:text-white">{{ pageTitle }}</h2>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Define la base del chatflow, su tipo de operación y la plantilla de arranque.
            </p>
          </div>

          <form class="space-y-5" [formGroup]="form" (ngSubmit)="guardar()">
            <div>
              <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Nombre visible</label>
              <input type="text" formControlName="name" placeholder="Ej: Soporte web principal" class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500" />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Nombre editable con el que identificas el chatflow en el panel. No se muestra a los visitantes del widget.</p>
              @if (hasError('name')) {
                <p class="mt-1 text-xs text-error-600 dark:text-error-400">{{ getError('name') }}</p>
              }
            </div>

            @if (isEditing && currentChatflow) {
              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Identificador interno (referencia técnica)</label>
                <div class="flex items-center gap-2">
                  <input type="text" [value]="currentChatflow.identifier" readonly
                    class="w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-mono text-gray-600 focus:outline-none dark:border-gray-800 dark:bg-gray-950/40 dark:text-gray-400" />
                  <button type="button" (click)="copiarIdentificador()" title="Copiar identificador"
                    class="shrink-0 rounded-lg border border-gray-300 p-2 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200">
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Slug único autogenerado, de solo lectura. Úsalo como referencia técnica y para buscar el chatflow en la lista.</p>
              </div>
            }

            <div>
              <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Descripción</label>
              <textarea rows="4" formControlName="description" placeholder="Objetivo, audiencia y contexto del flujo" class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"></textarea>
              @if (hasError('description')) {
                <p class="mt-1 text-xs text-error-600 dark:text-error-400">{{ getError('description') }}</p>
              }
            </div>

            <div class="grid gap-5 md:grid-cols-2">
              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo de chatflow</label>
                <select formControlName="chatflowType" class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                  @for (type of chatflowTypes; track type) {
                    <option [value]="type">{{ getTypeLabel(type) }}</option>
                  }
                </select>
                @if (hasError('chatflowType')) {
                  <p class="mt-1 text-xs text-error-600 dark:text-error-400">{{ getError('chatflowType') }}</p>
                }
                <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">{{ getTypeSummary(selectedType) }}</p>
                @if (selectedType === 'AI_CHATBOT') {
                  <div class="mt-2 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 dark:border-purple-700/40 dark:bg-purple-500/10">
                    <p class="text-xs font-semibold text-purple-700 dark:text-purple-400"> Próximamente — No disponible aún</p>
                    <p class="mt-0.5 text-[11px] text-purple-600 dark:text-purple-500">
                      Puedes crear el chatflow, pero no podrás activarlo hasta que la integración con IA generativa esté habilitada en esta versión.
                    </p>
                  </div>
                }
              </div>

              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Prioridad de resolución</label>
                <select formControlName="priority" class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                  @for (level of priorityLevels; track level.value) {
                    <option [ngValue]="level.value">{{ level.label }}</option>
                  }
                </select>
                <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">Cuando varios chatflows hacen match, gana el de mayor prioridad (Crítica sobre Baja).</p>
                @if (priorityAdjustedNote) {
                  <p class="mt-1 text-xs text-warning-600 dark:text-warning-400">{{ priorityAdjustedNote }}</p>
                }
                @if (hasError('priority')) {
                  <p class="mt-1 text-xs text-error-600 dark:text-error-400">{{ getError('priority') }}</p>
                }
              </div>
            </div>

            <div class="flex flex-wrap justify-end gap-3 pt-2">
              <button type="button" (click)="cancelar()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                Cancelar
              </button>
              @if (isEditing) {
                <button type="button" (click)="validarPublicacion()" class="rounded-lg border border-brand-300 px-4 py-2 text-sm font-medium text-brand-600 transition-colors hover:bg-brand-50 dark:border-brand-500/30 dark:text-brand-400 dark:hover:bg-brand-500/10">
                  {{ validating ? 'Validando...' : 'Validar publicación' }}
                </button>
              }
              <button type="submit" [disabled]="saving" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50">
                {{ saving ? 'Guardando...' : 'Guardar y abrir constructor' }}
              </button>
            </div>
          </form>
        </div>

        <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div class="mb-5">
            <h3 class="text-base font-semibold text-gray-800 dark:text-white">Biblioteca de plantillas</h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Elige una plantilla para materializar una base del flujo al guardar. También puedes empezar sin plantilla.
            </p>
          </div>

          <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <button
              type="button"
              (click)="form.patchValue({ templateId: null })"
              class="rounded-2xl border p-4 text-left transition-colors"
              [class.border-brand-500]="!form.get('templateId')?.value"
              [class.bg-brand-50]="!form.get('templateId')?.value"
              [class.dark:bg-brand-500/10]="!form.get('templateId')?.value"
              [class.border-gray-200]="form.get('templateId')?.value"
              [class.dark:border-gray-800]="form.get('templateId')?.value">
              <div class="flex items-center gap-2">
                <span class="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                  </svg>
                </span>
                <div class="text-sm font-semibold text-gray-800 dark:text-white">Empezar en blanco</div>
              </div>
              <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Crea el chatflow desde cero y define sus nodos, reglas y runtime manualmente.
              </p>
            </button>

            @for (template of templates; track template.id) {
              <button
                type="button"
                (click)="seleccionarTemplate(template)"
                class="rounded-2xl border p-4 text-left transition-colors"
                [class.border-brand-500]="isTemplateSelected(template.id)"
                [class.bg-brand-50]="isTemplateSelected(template.id)"
                [class.dark:bg-brand-500/10]="isTemplateSelected(template.id)"
                [class.border-gray-200]="!isTemplateSelected(template.id)"
                [class.dark:border-gray-800]="!isTemplateSelected(template.id)">
                @if (template.thumbnailUrl) {
                  <div class="mb-4 h-28 overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800">
                    <img [src]="template.thumbnailUrl" [alt]="template.name" class="h-full w-full object-cover" />
                  </div>
                }
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <div class="text-sm font-semibold text-gray-800 dark:text-white">{{ template.name }}</div>
                    @if (template.category) {
                      <div class="mt-1 text-xs uppercase tracking-wide text-brand-500">{{ getCategoryLabel(template.category) }}</div>
                    }
                  </div>
                  @if (template.defaultChatflowType) {
                    <span class="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                      {{ getTypeLabel(template.defaultChatflowType) }}
                    </span>
                  }
                </div>
                @if (template.description) {
                  <p class="mt-3 text-sm text-gray-500 dark:text-gray-400">{{ template.description }}</p>
                }
              </button>
            }
          </div>
        </div>
      </div>

      <div class="space-y-6">
        <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <h3 class="text-base font-semibold text-gray-800 dark:text-white">Resumen operativo</h3>
          <dl class="mt-4 space-y-4 text-sm">
            <div>
              <dt class="text-gray-500 dark:text-gray-400">Tipo seleccionado</dt>
              <dd class="mt-1 font-medium text-gray-800 dark:text-white">{{ getTypeLabel(selectedType) }}</dd>
            </div>
            <div>
              <dt class="text-gray-500 dark:text-gray-400">Plantilla</dt>
              <dd class="mt-1 font-medium text-gray-800 dark:text-white">
                {{ selectedTemplateDetail?.name || 'Sin plantilla' }}
              </dd>
            </div>
            <div>
              <dt class="text-gray-500 dark:text-gray-400">Estado</dt>
              <dd class="mt-1 font-medium text-gray-800 dark:text-white">
                {{ currentChatflow ? currentStatusLabel : 'Se creará en borrador' }}
              </dd>
            </div>
            @if (currentChatflow) {
              <div>
                <dt class="text-gray-500 dark:text-gray-400">Identificador interno</dt>
                <dd class="mt-1 break-all font-mono text-xs font-medium text-gray-600 dark:text-gray-400">{{ currentChatflow.identifier }}</dd>
              </div>
            }
          </dl>
        </div>

        <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div class="flex items-center justify-between">
            <h3 class="text-base font-semibold text-gray-800 dark:text-white">Detalle de plantilla</h3>
            @if (selectedTemplateLoading) {
              <span class="text-xs text-gray-400 dark:text-gray-500">Cargando...</span>
            }
          </div>

          @if (selectedTemplateDetail) {
            <div class="mt-4 space-y-4">
              <div>
                <div class="text-sm font-semibold text-gray-800 dark:text-white">{{ selectedTemplateDetail.name }}</div>
                @if (selectedTemplateDetail.description) {
                  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ selectedTemplateDetail.description }}</p>
                }
              </div>

              @if (selectedTemplateDetail.definitionJson) {
                <div>
                  <div class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Vista previa de la definición base</div>
                  <pre class="max-h-64 overflow-auto rounded-xl bg-gray-950 p-4 text-xs text-gray-200">{{ templateDefinitionPreview }}</pre>
                </div>
              }
            </div>
          } @else {
            <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Selecciona una plantilla para revisar su metadata y definición base.
            </p>
          }
        </div>

        @if (isEditing) {
          <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div class="flex items-center justify-between">
              <h3 class="text-base font-semibold text-gray-800 dark:text-white">Validación de publicación</h3>
              <button type="button" (click)="validarPublicacion()" class="text-sm font-medium text-brand-500 hover:text-brand-600">
                {{ validating ? 'Validando...' : 'Volver a validar' }}
              </button>
            </div>

            @if (validationError) {
              <div class="mt-4 rounded-xl border border-error-200 bg-error-50 p-4 text-sm text-error-600 dark:border-error-500/20 dark:bg-error-500/10 dark:text-error-400">
                {{ validationError }}
              </div>
            }

            @if (currentChatflow?.validationStatus !== 'PENDING') {
              <div class="mt-4 space-y-4">
                <div class="rounded-xl border p-4"
                  [class.border-success-200]="currentChatflow?.validationStatus === 'VALID'"
                  [class.bg-success-50]="currentChatflow?.validationStatus === 'VALID'"
                  [class.dark:border-success-500/20]="currentChatflow?.validationStatus === 'VALID'"
                  [class.dark:bg-success-500/10]="currentChatflow?.validationStatus === 'VALID'"
                  [class.border-warning-200]="currentChatflow?.validationStatus === 'INVALID'"
                  [class.bg-warning-50]="currentChatflow?.validationStatus === 'INVALID'"
                  [class.dark:border-warning-500/20]="currentChatflow?.validationStatus === 'INVALID'"
                  [class.dark:bg-warning-500/10]="currentChatflow?.validationStatus === 'INVALID'">
                  <div class="text-sm font-semibold"
                    [class.text-success-700]="currentChatflow?.validationStatus === 'VALID'"
                    [class.dark:text-success-400]="currentChatflow?.validationStatus === 'VALID'"
                    [class.text-warning-700]="currentChatflow?.validationStatus === 'INVALID'"
                    [class.dark:text-warning-400]="currentChatflow?.validationStatus === 'INVALID'">
                    {{ currentChatflow?.validationStatus === 'VALID' ? 'Listo para publicar' : 'Aún no cumple las validaciones' }}
                  </div>
                </div>

                @if (currentChatflow?.validationErrors && currentChatflow!.validationErrors!.length) {
                  <ul class="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                    @for (issue of currentChatflow!.validationErrors; track $index) {
                      <li>• @if (issue.nodeKey) {<span class="font-medium">[{{ issue.nodeKey }}]</span> }{{ issue.message }}</li>
                    }
                  </ul>
                } @else {
                  <p class="text-sm text-success-700 dark:text-success-400">
                    No se encontraron observaciones. Puedes activarlo desde el listado o seguir trabajando el flujo.
                  </p>
                }
              </div>
            } @else {
              <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Ejecuta la validación para verificar nodos de entrada, ramas mínimas y consistencia del grafo.
              </p>
            }
          </div>
        }
      </div>
    </div>
  }
</div>
